import {
  useCustomMutation,
  useInvalidate,
  useNotification,
  useTranslate,
} from '@refinedev/core';
import { Modal } from 'antd';
import type {
  ReviewRejectIn,
  ReviewRejectionReason,
} from '@/api/generated/schemas';

const { confirm } = Modal;

interface UseReviewActionsProps {
  reviewId?: string | number;
}

interface ReviewAction {
  mutate: () => void;
  isLoading: boolean;
}

interface ReviewRejectAction {
  mutate: (reason: ReviewRejectionReason, onSuccess?: () => void) => void;
  isLoading: boolean;
}

interface ReviewActions {
  accept: ReviewAction;
  reject: ReviewRejectAction;
}

export const useReviewActions = ({
  reviewId,
}: UseReviewActionsProps): ReviewActions => {
  const t = useTranslate();
  const invalidate = useInvalidate();
  const { open } = useNotification();

  const {
    mutate: acceptReview,
    mutation: { isPending: isAccepting },
  } = useCustomMutation();
  const {
    mutate: rejectReview,
    mutation: { isPending: isRejecting },
  } = useCustomMutation();

  const handleAccept = () => {
    if (!reviewId) return;

    confirm({
      title: t('feedback.reviews.actions.confirm_accept'),
      onOk: () => {
        acceptReview(
          {
            url: `feedback/reviews/${reviewId}/accept`,
            method: 'post',
            values: {},
          },
          {
            onSuccess: () => {
              invalidate({
                resource: 'feedback.reviews',
                invalidates: ['detail', 'list'],
                id: reviewId,
              });
              open?.({
                type: 'success',
                message: t('notifications.success'),
                description: t('feedback.reviews.actions.accept'),
              });
            },
          }
        );
      },
    });
  };

  // The reason picker modal is the confirmation step, so no extra confirm here.
  const handleReject = (
    reason: ReviewRejectionReason,
    onSuccess?: () => void
  ) => {
    if (!reviewId) return;

    const values: ReviewRejectIn = { reason };
    rejectReview(
      {
        url: `feedback/reviews/${reviewId}/reject`,
        method: 'post',
        values,
      },
      {
        onSuccess: () => {
          invalidate({
            resource: 'feedback.reviews',
            invalidates: ['detail', 'list'],
            id: reviewId,
          });
          open?.({
            type: 'success',
            message: t('notifications.success'),
            description: t('feedback.reviews.actions.reject'),
          });
          onSuccess?.();
        },
      }
    );
  };

  return {
    accept: {
      mutate: handleAccept,
      isLoading: isAccepting,
    },
    reject: {
      mutate: handleReject,
      isLoading: isRejecting,
    },
  };
};
